import { createColumnHelper, RowModel, Table } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { IVehicle, VehiclePipelineStage } from "../../models/Vehicle";
import openInNewTab from "../../utils/openInNewTab";
import VehicleComplexityDropdown from "./VehicleComplexityDropdown";
import VehicleLocationDropdown from "./VehicleLocationDropdown";
import PartVehicleDetails from "../leads/LeadParts/PartVehicleDetails";
import { HTMLProps, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { PageMode } from "../../models/PagedTable";
import PartVehicleImages from "../leads/LeadParts/LeadImagesCarousel";
import VehicleJobsTableCell from "./VehicleJobsTableCell";
import BulkFinancialDecisionModal from "./BulkFinancialDecisionModal";
import TableVehicleDetails from "./VehiclesTable/TableVehicleDetails";
import VehicleCollectedCODStatus from "../vehiclesCollected/VehicleCollectedCODStatus";
import VehiclePagedTable from "../shared/Tables/VehiclePagedTable";
import ButtonGroup from "../shared/ButtonGroup";
import VehicleConfirmationModal, { IBulkResponse, IBulkVehicleAction } from "./VehicleConfirmationModal";
import VehicleStatusCell from "./VehicleStatusCell";
import VehicleWorkflowCell from "./VehicleWorkflowCell";

import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useLeads from "../../data/useLeads";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import usePayments from "../../data/usePayments";
import useEmails from "../../data/useEmails";
import VehicleOrders from "./VehicleOrders";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import BulkAddToWorkOrderModal from "../workOrders/BulkAddToWorkOrderModal";
import { SearchPaletteItem } from "../shared/Inputs/SearchPaletteTypes";
import VehicleSearchFilters from "./VehicleSearchFilters";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import useVehicleStats from "../../data/useVehicleStats";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import printHtml from "../../utils/printHtml";
import useNotification from "../notifications/useNotifications";

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-3 cursor-pointer border-gray-400'}
      {...rest}
    />
  )
}

const colHelper = createColumnHelper<IVehicle>();


type VehicleTableProps = {
  additionalBody?: Record<string, unknown>;
  toolbarActions?: ReactNode;
  // Chip to start on instead of the default - see VehicleList's `?filter=` handling.
  initialFilterId?: string;
};

// The "Crushed" chip's id below, for deep links.
export const CRUSHED_FILTER_ID_TME = "11";

export default function VehicleTableTme({ additionalBody, toolbarActions, initialFilterId }: VehicleTableProps) {
  const { t } = useTranslation("vehicles");
  const [selectedVehicles, setSelectedVehicles] = useState<IVehicle[]>([]);
  const [selectedAction, setSelectedAction] = useState<IBulkVehicleAction | null>(null);
  const [isBulkWorkOrderOpen, setIsBulkWorkOrderOpen] = useState(false);
  const [workOrderItems, setWorkOrderItems] = useState<SearchPaletteItem[]>([]);
  const [searchBody, setSearchBody] = useState<Record<string, any>>({});
  const [financialDecisionOpen, setFinancialDecisionOpen] = useState(false);
  const [financialDecisionVehicles, setFinancialDecisionVehicles] = useState<IVehicle[]>([]);

  const hasTmeFeature = CheckFeatureFlag("TME");

  const { stats } = useVehicleStats();

  // Count of vehicles per pipeline stage, keyed by stage for quick lookup when building filter labels.
  const pipelineCounts = useMemo(() => {
    const counts = new Map<VehiclePipelineStage, number>();
    stats.data?.data?.pipelineCounts?.forEach((pc) => counts.set(pc.stage, pc.count));
    return counts;
  }, [stats.data]);

  // Appends " (n)" to a pipeline filter label once the stat count is available.
  const pipelineLabel = (label: string, stage: VehiclePipelineStage) => {
    const count = pipelineCounts.get(stage);
    return count === undefined ? label : `${label} (${count})`;
  };

  const { saveHandlers } = useDefaultCRUDHandlers(t("Vehicles"));
  const { bulkComplete, bulkSubmitCod } = useLeads(true, "VehicleTable");
  const { bulkMarkCollected } = useVehiclesMutations();
  const { bulkSendPayments } = usePayments();
  const {
    BulkSendDocumentsEmails,
    BulkSendDocumentRequestEmails,
    BulkSendBookingConfirmationEmails
  } = useEmails();
  const { generateVehicleLabel } = useLabelGenerator();
  const { addNotification } = useNotification();

  const handleBulkPrintLabels = async (vehicles: IVehicle[]) => {
    if (vehicles.length === 0) return;

    try {
      const htmlPages = await Promise.all(vehicles.map((vehicle) => generateVehicleLabel(vehicle)));
      printHtml(htmlPages.join("<div style='page-break-after: always;'></div>"));
    } catch {
      addNotification({ variant: "error", primaryText: t("Unable to print vehicle labels") });
    }
  };


  function executeAction(body: any, mutation: any): Promise<IBulkResponse[]> {
    return new Promise<IBulkResponse[]>((resolve, reject) => {
      mutation.mutate(body, {
        onSuccess: (res: any) => {
          saveHandlers.onSuccess();
          resolve(res);
        },
        onError: (res: any) => {
          saveHandlers.onError();
          resolve([]);
        }
      });
    });
  }

  function executeLeadAction(body: any, mutation: any, vehicles: IVehicle[]): Promise<IBulkResponse[]> {
    return new Promise<IBulkResponse[]>((resolve, reject) => {
      mutation.mutate(body, {
        onSuccess: (res: any) => {
          saveHandlers.onSuccess();
          // returns lead ids, so map back to vehicles
          resolve(res.map((bulkResponse: IBulkResponse) => {
            return {
              ...bulkResponse,
              data: vehicles.find(v => v.leads[0]?.id === bulkResponse.data)?.id || ""
            }
          }));
        },
        onError: (res: any) => {
          saveHandlers.onError();
          resolve([]);
        }
      });
    });
  }

  // #region Vehicle Actions
  const vehicleActions: IBulkVehicleAction[] = [
    {
      title: t("Mark Vehicles as Collected"),
      action: async (vehicles: IVehicle[]) => {
        const body = {
          vehicleIds: vehicles.map(v => v.id).filter(id => id !== undefined) as string[]
        }

        return executeAction(body, bulkMarkCollected);
      },
      buttonText: t("Mark as Collected")
    },
    // --------------------------- //
    {
      title: t("Auto Complete Vehicles"),
      body: t("This will raise a payment for this lead and attempt to COD the vehicle."),
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leads: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }

        return executeLeadAction(body, bulkComplete, vehicles);

      },
      buttonText: t("Auto Complete")
    },
    // --------------------------- //
    {
      title: t("Submit COD for Vehicles"),
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leads: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }

        return executeLeadAction(body, bulkSubmitCod, vehicles);
      },
      buttonText: t("Submit COD")
    },
    // --------------------------- //
    {
      title: t("Request Bank Details & ID for Vehicles"),
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leadIds: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }
        return executeLeadAction(body, BulkSendDocumentRequestEmails, vehicles);
      },
      buttonText: t("Request Bank Details & ID")
    },
    // --------------------------- //
    {
      title: t("Raise Payment for Vehicles"),
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leadIds: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }

        return executeLeadAction(body, bulkSendPayments, vehicles);
      },
      buttonText: t("Raise Payment")
    },
    // --------------------------- //
    {
      title: t("Send Attachments for Vehicles"),
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leadIds: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }

        return executeLeadAction(body, BulkSendDocumentsEmails, vehicles);
      },
      buttonText: t("Send Attachments")
    },
    // --------------------------- //
    {
      title: t("Resend Confirmation for Vehicles"),
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leadIds: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }

        return executeLeadAction(body, BulkSendBookingConfirmationEmails, vehicles);
      },
      buttonText: t("Resend Confirmation")
    },
    // --------------------------- //
    // TODO: Implement Review Request as per Adam and Ben's feedback
    // {
    //   title: "Request Review for Vehicles",
    //   action: async (vehicles: IVehicle[]) => { return [] },
    //   buttonText: "Request Review"
    // },
  ];
  // #endregion

  // #region Expanded Row
  const expandedRow = (vehicle: IVehicle) => {
    return (
      <div className="flex flex-col gap-y-2">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-4 bg-gray-50 p-2 rounded-lg border border-gray-300">
          <ButtonGroup
            options={vehicleActions.map((groupedButton, index) => ({
              id: index.toString(),
              label: groupedButton.buttonText,
              value: groupedButton.buttonText, // Not used in action mode, but required by interface
              disabled: false,
              onClick: () => {
                setSelectedVehicles([vehicle]);
                setSelectedAction(groupedButton);
              },
            }))}
            actionMode={true}
            size="md"
          />
        </div>

        <PartVehicleDetails vehicle={vehicle} />

        {/* TEMP FIX TO AVOID STUPID WIDTH */}
        <div className="!max-w-[85vw]">
          <PartVehicleImages lead={vehicle.leads[0]} />
        </div>

        <VehicleOrders vehicleId={vehicle.id} />
      </div>
    )
  };
  // #endregion

  // #region Columns
  const cols = useMemo(
    () => [
      TableCheckboxCol(true, true),
      colHelper.accessor("vrm", {
        header: t("Details"),
        id: "details",
        sortingFn: "auto",
        cell: (info) => <TableVehicleDetails vehicle={info.row.original!} />,
      }),
      colHelper.display({
        header: t("Vehicle Status"),
        cell: (info) => <div className="flex flex-col space-y-4">
          <span></span>
          <VehicleStatusCell status={info.row.original.status} statusDate={info.row.original.statusDate} />
          {info.row.original.leads?.length > 0 && (
            <>
              {/* <VehicleCodCell vehicle={info.row.original} /> */}
              <VehicleCollectedCODStatus leadId={info.row.original.leads[0].id} vehicle={info.row.original} classes="" />
            </>
          )}
        </div>,
      }),
      colHelper.display({
        header: t("Jobs"),
        enableSorting: false,
        cell: (info) => <VehicleJobsTableCell lead={info.row.original.leads[0]} vehicle={info.row.original} />,
      }),
      colHelper.accessor("yardStatus", {
        header: t("Yard"),
        enableSorting: false,
        cell: (info) => <div id={info.row.original.id}>
          <p className="mt-2">{t("Yard Location")}</p>
          <VehicleLocationDropdown vehicle={info.row.original!} size="sm" classes="h-8 w-40 text-sm" />
          <p className="mt-2">{t("Complexity")}</p>
          <VehicleComplexityDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
        </div>
      }),
      colHelper.display({
        header: t("Workflow"),
        enableSorting: false,
        cell: (info) => <VehicleWorkflowCell workflow={info.row.original.workflow} />,
      }),
    ],
    [t]
  );
  // #endregion

  // #region Render
  return (
    <>
      <VehicleConfirmationModal
        vehicles={selectedVehicles}
        bulkAction={selectedAction}
        open={selectedAction !== null}
        setOpen={(open: boolean) => {
          if (!open) {
            setSelectedAction(null);
            setSelectedVehicles([]);
          }
        }}
      />
      <BulkAddToWorkOrderModal
        open={isBulkWorkOrderOpen}
        onClose={() => {
          setIsBulkWorkOrderOpen(false);
          setWorkOrderItems([]);
        }}
        selectedItems={workOrderItems}
      />

      <BulkFinancialDecisionModal
        open={financialDecisionOpen}
        vehicles={financialDecisionVehicles}
        onClose={() => {
          setFinancialDecisionOpen(false);
          setFinancialDecisionVehicles([]);
        }}
      />

      <VehicleSearchFilters onApply={setSearchBody} onClear={() => setSearchBody({})} />

      {toolbarActions && <div className="my-3">{toolbarActions}</div>}

      <VehiclePagedTable
        columns={cols}
        url="/vehicle"
        pageMode={PageMode.Stock}// not using this
        queryLabel={t("Search vehicles VRM, Stock Number, VIN, Make, Model, Engine Code or Location Name")}
        expandedRow={(vehicle) => expandedRow(vehicle)}
        onRowClick={(x: IVehicle) => openInNewTab(`vehicles/${x.id}`)}
        filters={[
          { id: "1", label: pipelineLabel(t("B2B"), VehiclePipelineStage.B2B), value: { pipeline: VehiclePipelineStage.B2B }, type: "searchBody", default: true },
          { id: "2", label: pipelineLabel(t("Targeting"), VehiclePipelineStage.Targeting), value: { pipeline: VehiclePipelineStage.Targeting }, type: "searchBody" },
          { id: "3", label: pipelineLabel(t("Purchased"), VehiclePipelineStage.NotOnSite), value: { pipeline: VehiclePipelineStage.NotOnSite }, type: "searchBody" },
          { id: "4", label: pipelineLabel(t("Pre-Triage"), VehiclePipelineStage.PreTriage), value: { pipeline: VehiclePipelineStage.PreTriage }, type: "searchBody" },
          { id: "5", label: pipelineLabel(t("Stored"), VehiclePipelineStage.Stored), value: { pipeline: VehiclePipelineStage.Stored }, type: "searchBody" },
          { id: "6", label: pipelineLabel(t("Awaiting Dismantling"), VehiclePipelineStage.AwaitingDismantling), value: { pipeline: VehiclePipelineStage.AwaitingDismantling }, type: "searchBody" },
          { id: "7", label: pipelineLabel(t("Dismantling"), VehiclePipelineStage.Dismantling), value: { pipeline: VehiclePipelineStage.Dismantling }, type: "searchBody" },
          { id: "8", label: pipelineLabel(t("Dismantled"), VehiclePipelineStage.Dismantled), value: { pipeline: VehiclePipelineStage.Dismantled }, type: "searchBody" },
          { id: "9", label: pipelineLabel(t("Completed"), VehiclePipelineStage.Complete), value: { pipeline: VehiclePipelineStage.Complete }, type: "searchBody" },
          { id: "10", label: pipelineLabel(t("Archived"), VehiclePipelineStage.Archived), value: { pipeline: VehiclePipelineStage.Archived }, type: "searchBody" },
          { id: CRUSHED_FILTER_ID_TME, label: pipelineLabel(t("Crushed"), VehiclePipelineStage.Scrapped), value: { pipeline: VehiclePipelineStage.Scrapped }, type: "searchBody" },
          // Crushed vehicles only show under their own chip — "All" excludes them.
          { id: "12", label: t("All"), value: { excludeScrapped: true }, type: "searchBody" },
        ]}
        initialFilterId={initialFilterId}
        actionButtons={[
          {
            label: t("Bulk Actions"),
            onClick: () => { },
            groupedButtons: vehicleActions.length > 0 ? vehicleActions.map((action) => ({
              label: action.buttonText,
              disabledOnNoRows: true,
              onClick: (rows: RowModel<IVehicle>, table: Table<IVehicle>) => {
                setSelectedVehicles(table.getSelectedRowModel().flatRows.map(row => row.original));
                setSelectedAction(action);
              },
            })).concat([
              {
                label: t("Add to work order"),
                disabledOnNoRows: true,
                onClick: (_rows: RowModel<IVehicle>, table: Table<IVehicle>) => {
                  const selected = table.getSelectedRowModel().flatRows.map((row) => row.original);
                  const items: SearchPaletteItem[] = selected
                    .filter((vehicle) => Boolean(vehicle.id))
                    .map((vehicle) => ({
                      id: vehicle.id!,
                      name: `${vehicle.vrm ?? "Unknown VRM"}${vehicle.combinedMake ? ` - ${vehicle.combinedMake}` : ""}${vehicle.combinedModel ? ` ${vehicle.combinedModel}` : ""}`,
                      type: "Vehicle",
                    }));

                  setWorkOrderItems(items);
                  setIsBulkWorkOrderOpen(true);
                },
              },
              {
                label: t("Print Vehicle Labels"),
                disabledOnNoRows: true,
                onClick: (_rows: RowModel<IVehicle>, table: Table<IVehicle>) => {
                  handleBulkPrintLabels(table.getSelectedRowModel().flatRows.map((row) => row.original));
                },
              },
              ...(hasTmeFeature ? [{
                label: t("Update Financial Decision"),
                disabledOnNoRows: true,
                onClick: (_rows: RowModel<IVehicle>, table: Table<IVehicle>) => {
                  const selected = table.getSelectedRowModel().flatRows.map((row) => row.original);
                  setFinancialDecisionVehicles(selected);
                  setFinancialDecisionOpen(true);
                },
              }] : []),
            ]) : [],
          }
        ]}
        additionalBody={{
          includeParts: true,
          includePartImages: true,
          includeCatalyticResults: true,
          includeExportOffers: true,
          ...searchBody,
          ...(additionalBody ?? {})
        }}
      />
    </>
  );
  // #endregion
}
